import { FormikErrors, FormikHelpers, useFormik } from 'formik'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { Input, PopoverInput } from '@ui/components/Input'
import { Modal } from '@ui/components/Modal'

import { isAllowedNumericKey } from '@utils/input.utils'
import { isNumber, maxNumber, minNumber } from '@utils/validations'

import { NameDto } from '@model/NameDto'

interface RebateSettingsModalProps {
  onSubmit: (values: Partial<RebateSettingsFormValues>) => Promise<void>
  initialValues?: Partial<RebateSettingsFormValues>
  approvalTypes: NameDto<number>[]
  rebatePeriods: NameDto<number>[]
}

export const RebateSettingsModal: BaseModalType<RebateSettingsModalProps> = (props) => {
  const { onSubmit, close, initialValues, approvalTypes, rebatePeriods } = props

  const { t } = useTranslation()

  const { setFieldValue, errors, values, handleSubmit, setErrors, isSubmitting } =
    useRebateSettingsForm({
      initialValues: initialValues ?? DEFAULT_VALUES,
      handleSubmit: async (values, { resetForm, setSubmitting }) => {
        setSubmitting(true)
        await onSubmit(values)
        setSubmitting(false)
        resetForm()
      },
    })

  const hasErrors = useMemo(() => Object.values(errors).some((error) => !!error), [errors])

  const isApplyDisabled =
    hasErrors ||
    isSubmitting ||
    !values.rebateApprovalTypeId ||
    values.rebatePeriodId === undefined ||
    !values.rebateAmountInPercentage

  const handleChange = (key: string, value: any) => {
    setFieldValue(key, value)

    if (hasErrors) {
      setErrors({
        ...errors,
        [key]: undefined,
      })
    }
  }

  return (
    <Modal size='sm'>
      <Modal.Header>{t('IB.RebatesPage.ClientsPage.Update Rebate settings')}</Modal.Header>
      <Modal.Body>
        <PopoverInput
          value={values.rebateApprovalTypeId}
          onChange={(option) => handleChange('rebateApprovalTypeId', option.id)}
          options={approvalTypes}
          isFullWidth
          label={t('Type')}
          hint={errors.rebateApprovalTypeId}
          error={!!errors.rebateApprovalTypeId}
        />
        <PopoverInput
          value={values.rebatePeriodId}
          onChange={(option) => handleChange('rebatePeriodId', option.id)}
          options={rebatePeriods}
          isFullWidth
          label={t('IB.RebatesPage.Payout period')}
          hint={errors.rebatePeriodId}
          error={!!errors.rebatePeriodId}
        />
        <Input
          value={values.rebateAmountInPercentage}
          label={t('IB.RebatesPage.Rebate amount in %')}
          onChange={(e) => handleChange('rebateAmountInPercentage', e.target.value)}
          hint={errors.rebateAmountInPercentage}
          fullWidth
          error={!!errors.rebateAmountInPercentage}
          onKeyDown={(e) => !isAllowedNumericKey(e) && e.preventDefault()}
        />
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary disabled={isApplyDisabled} onClick={() => handleSubmit()}>
          {t('Apply')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}

export interface RebateSettingsFormValues {
  rebateApprovalTypeId: number
  rebatePeriodId: number
  rebateAmountInPercentage: string
  tradingAccountId: string
}

interface RebateSettingsFormParams {
  handleSubmit: (
    values: Partial<RebateSettingsFormValues>,
    formikHelpers: FormikHelpers<Partial<RebateSettingsFormValues>>
  ) => void
  initialValues?: Partial<RebateSettingsFormValues>
}

const DEFAULT_VALUES: Partial<RebateSettingsFormValues> = {
  rebateApprovalTypeId: undefined,
  rebatePeriodId: undefined,
  rebateAmountInPercentage: undefined,
}

export const useRebateSettingsForm = ({
  handleSubmit,
  initialValues = DEFAULT_VALUES,
}: RebateSettingsFormParams): ReturnType<typeof useFormik<Partial<RebateSettingsFormValues>>> => {
  const { t } = useTranslation()

  return useFormik<Partial<RebateSettingsFormValues>>({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validate: (values) => {
      const errors: FormikErrors<RebateSettingsFormValues> = {}

      if (values.rebateApprovalTypeId === undefined) {
        errors.rebateApprovalTypeId = t('Validation.Required')
      }

      if (values.rebatePeriodId === undefined) {
        errors.rebatePeriodId = t('Validation.Required')
      }

      if (!values.rebateAmountInPercentage) {
        errors.rebateAmountInPercentage = t('Validation.Required')
      }

      if (values.rebateAmountInPercentage && !isNumber(values.rebateAmountInPercentage)) {
        errors.rebateAmountInPercentage = t(
          'Validation.Maximum {{amount}} decimals allowed for input',
          {
            amount: 2,
          }
        )
      }

      if (
        values.rebateAmountInPercentage &&
        (minNumber(values.rebateAmountInPercentage, 0.1) ||
          maxNumber(values.rebateAmountInPercentage, 100))
      ) {
        errors.rebateAmountInPercentage = t(
          'IB.RebatesPage.ApprovalsPage.Please enter a valid percentage between 0 and 100'
        )
      }

      return errors
    },
    validateOnChange: false,
  })
}
